import React, { useEffect, useState } from 'react'
import { Button } from 'reactstrap'
import LanguageTableInstructions from './components/LanguageTableInstructions'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import LanguageEditModal from '../modals/LanguageEditModal'
import { api } from '../shared/ApiProvider'

export default function LanguageTable() {
  const [langTableArray, setLangTableArray] = useState(null)
  const [languageIndex, setLanguageIndex] = useState(null)
  const [tempSuggestion, setTempSuggestion] = useState(null)
  const [tempSpecificLanguage, setTempSpecificLanguage] = useState(null)
  const [showLangEditModal, setShowLangEditModal] = useState(false)
  const [iso3Languages, setIso3Languages] = useState([])
  const [iso2Languages, setIso2Languages] = useState([])
  const [addingNewRow, setAddingNewRow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    api
      .get('/crosswalkTable')
      .then((resp) => {
        setIsLoading(false)
        setLangTableArray(resp.data)
      })
      .catch((error) => {
        setIsLoading(false)
      })
    api
      .get('/public/languages', {
        headers: { 'Access-Control-Allow-Origin': '*' },
      })
      .then(
        (langResponse) => {
          const languages = langResponse.data.map((l) => ({
            value: l.code, //l.id,
            label: l.language,
          }))
          setIso2Languages(languages)
          return languages
        },
        (error) => {
          console.error(error)
        },
      )
    api
      .get('/public/ISO_639-3', {
        headers: { 'Access-Control-Allow-Origin': '*' },
      })
      .then(
        (lang3Response) => {
          const languages = lang3Response.data.map((l) => ({
            value: l.id, //l.id,
            label: l.name,
          }))
          setIso3Languages(languages)
          return languages
        },
        (error) => {
          console.error(error)
        },
      )
  }, [])
  const onChangeSpecificLanguage = (values) => {
    setTempSpecificLanguage(values)
  }
  const onChangeSuggestions = (values) => {
    setTempSuggestion(values)
  }

  const editRow = (e) => {
    let specLangIndex = +e.target.dataset.lang_index
    setLanguageIndex(specLangIndex)
    let tempSpecLang = langTableArray[specLangIndex].langCode.toLowerCase()
    let tempSpec = iso3Languages.filter(
      (option) => option.value == tempSpecLang.toLowerCase(),
    )[0]
    setTempSpecificLanguage(tempSpec)

    let tempSugLang =
      langTableArray[specLangIndex].suggestedLanguage.code.toLowerCase()
    let tempSug = iso2Languages.filter(
      (option) => option.value == tempSugLang.toLowerCase(),
    )[0]
    setTempSuggestion(tempSug)
    setShowLangEditModal(true)
  }

  const addRow = (e) => {
    setAddingNewRow(true)
    setShowLangEditModal(true)
    setTempSpecificLanguage()
    setTempSuggestion()
    setLanguageIndex(langTableArray.length)
  }

  const saveLanguage = () => {
    if (tempSpecificLanguage != null && tempSuggestion != null) {
      let specLang = tempSpecificLanguage.value.toUpperCase()
      let specLangName = tempSpecificLanguage.label
      let sugLang = tempSuggestion.value.toUpperCase()
      let sugLangName = tempSuggestion.label

      if (!addingNewRow) {
        let crossWalkId = langTableArray[languageIndex].id
        // update
        let payload = {
          id: crossWalkId,
          langCode: specLang,
          name: specLangName,
          suggestedLanguage: { code: sugLang, language: sugLangName },
        }
        api.put('/crosswalkTable', payload).then(() => {
          alert('submit successful!')
          setShowLangEditModal(false)
        })
      } else if (tempSpecificLanguage != null && tempSuggestion != null) {
        // update
        let payload = {
          id: null,
          langCode: specLang,
          name: specLangName,
          suggestedLanguage: { code: sugLang, language: sugLangName },
        }
        api.put('/crosswalkTable', payload).then(() => {
          alert('submit successful!')
          setShowLangEditModal(false)
        })
        // {language_id: crossWalkId, language_code: tempSpecificLanguage.value, eden_code:tempSuggestion.value}
      }
    } else {
      alert('Not enough information provided')
    }
  }
  const cancelNewLanguage = () => {
    setShowLangEditModal(false)
    setAddingNewRow(false)
  }

  // TanStack Table migration
  const columns = [
    {
      header: 'Specific Language (ISO 639-3)',
      accessorKey: 'specificLanguage',
      cell: (info) => (
        <span>
          {info.row.original.name}{' '}
          <em style={{ opacity: '.4' }}>{info.row.original.langCode}</em>
        </span>
      ),
    },
    {
      header: 'Suggested Language (ISO 639-2)',
      accessorKey: 'suggestedLanguage',
      cell: (info) => (
        <span>
          {info.row.original.suggestedLanguage.language}{' '}
          <em style={{ opacity: '.4' }}>
            {info.row.original.suggestedLanguage.code}
          </em>
        </span>
      ),
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: (info) => (
        <Button data-lang_index={info.row.index} onClick={editRow}>
          Edit
        </Button>
      ),
    },
  ]

  const table = useReactTable({
    data: langTableArray || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div>
      <h5 className="text-center mb-0 p-2">Language Table</h5>
      <a
        href="#language-table-instructions"
        data-toggle="collapse"
        className="m-0 mb-2 align-self-end"
        size="sm"
      >
        What's this?
      </a>
      <hr />
      <LanguageTableInstructions />
      {isLoading ? (
        <h5 className="text-center mb-0 p-2 alert-warning">Loading...</h5>
      ) : null}
      <table className="table table-striped table-bordered table-hover mt-3">
        <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className="pointer-hover">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </th>
            ))}
          </tr>
        ))}
        </thead>
        <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
        </tbody>
      </table>
      {langTableArray ? (
        <LanguageEditModal
          saveFunction={saveLanguage}
          cancelFunction={cancelNewLanguage}
          index={languageIndex}
          optionsOne={iso3Languages}
          setterOne={onChangeSpecificLanguage}
          tempValueOne={tempSpecificLanguage}
          optionsTwo={iso2Languages}
          setterTwo={onChangeSuggestions}
          tempValueTwo={tempSuggestion}
          show={showLangEditModal}
        />
      ) : null}
      <Button onClick={addRow}>Add Language</Button>
    </div>
  )
}
